import { useCallback, useEffect, useRef } from "react";
import { useStore } from "react-redux";
import uploadManager from "../services/UploadManager";
import { checkIsfileUploading } from "../utils/helper";

// 8 hours in milliseconds
const AUTO_REFRESH_INTERVAL = 8 * 60 * 60 * 1000;

// 1 hour in milliseconds
const AUTO_REFRESH_VERIFICATION_INTERVAL = 60 * 60 * 1000;

// 10 minutes in milliseconds
const MIN_INACTIVITY_TIME = 10 * 60 * 1000;

export default function useApp() {
    const store = useStore();
    const pageLoadTimeRef = useRef(Date.now());
    const lastActivityTimeRef = useRef(Date.now());

    useEffect(() => {
        uploadManager.setStore(store);
    }, [store]);

    const handleBeforeUnload = useCallback((event) => {
        const state = store.getState();
        const isFilesUploading = checkIsfileUploading(state.fileUpload);
        if (isFilesUploading) {
            event.preventDefault();
            event.returnValue = 'Files are currently uploading. Are you sure you want to leave?';
            return event.returnValue;
        }
    }, [store]);

    useEffect(() => {
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [handleBeforeUnload]);

    useEffect(() => {
        const updateActivityTime = () => {
            lastActivityTimeRef.current = Date.now();
        };

        // Track user activity events
        const activityEvents = ['mousedown', 'keypress', 'scroll', 'touchstart', 'click'];
        activityEvents.forEach(event => {
            document.addEventListener(event, updateActivityTime, { passive: true });
        });

        const checkAndRefresh = () => {
            const now = Date.now();
            const timeSincePageLoad = now - pageLoadTimeRef.current;
            const timeSinceLastActivity = now - lastActivityTimeRef.current;
            if (timeSincePageLoad >= AUTO_REFRESH_INTERVAL && timeSinceLastActivity >= MIN_INACTIVITY_TIME) {
                if (!document.hidden) {
                    window.location.reload();
                }
            }
        };

        const intervalId = setInterval(checkAndRefresh, AUTO_REFRESH_VERIFICATION_INTERVAL);

        const handleVisibilityChange = () => {
            if (!document.hidden) {
                checkAndRefresh();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        checkAndRefresh();

        return () => {
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            activityEvents.forEach(event => {
                document.removeEventListener(event, updateActivityTime);
            });
        };
    }, []);
}
